import { SelectDropdown } from '../shared/SelectDropdown';
/**
 * Team members + invitations section for the Account/Settings page.
 *
 * Owners and admins see:
 *   - the current member roster (with role badges)
 *   - the requests to join from same-domain signups (Approve / Decline; spec 2026-09-10)
 *   - the pending invitations list (with revoke buttons)
 *   - an invite form (email + role)
 *
 * Non-admin members see a read-only roster.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Avatar } from '../ui/Avatar';
import { LoadError } from '../shared/IssuesLoadError';
import { useAppSession } from '../../hooks/useAppSession';
import {
  useTeamMembers,
  useTeamInvitations,
  useInviteTeamMember,
  useRevokeTeamInvitation,
  useRemoveTeamMember,
  useLeaveTeam,
  useTeamJoinRequests,
  useApproveJoinRequest,
  useDeclineJoinRequest,
} from '../../hooks/useTeams';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { InviteRefusalMessage } from '../shared/InviteRefusalMessage';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { INVITE_FORM_COPY, INVITE_ROLE_OPTIONS, inviteEmailError, isValidInviteEmail } from '@/lib/inviteEmail';

log.info('[TeamMembersSection] Module loaded');

type RoleBadgeProps = { role: 'owner' | 'admin' | 'member' };

function RoleBadge({ role }: RoleBadgeProps) {
  const styles: Record<RoleBadgeProps['role'], string> = {
    owner: 'bg-primary-50 text-primary-700 border-primary-200',
    admin: 'bg-[var(--color-type-a11y-bg)] text-[var(--color-type-a11y)] border-[#c4b5fd]',
    member: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${styles[role]}`}
    >
      {role}
    </span>
  );
}

// Cheap client-side sanity check; the server validates with Zod.

/** "asked today" / "asked 1 day ago" / "asked N days ago", for a request's created_at. */
function formatAskedAgo(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffDays = Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'asked today';
  if (diffDays === 1) return 'asked 1 day ago';
  return `asked ${diffDays} days ago`;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = ts - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'expires today';
  if (diffDays === 1) return 'expires in 1 day';
  return `expires in ${diffDays} days`;
}

export function TeamMembersSection() {
  const { data: session, isLoading: sessionLoading } = useAppSession();
  const teamId = session?.team?.id;
  const callerRole = session?.team?.role;
  const canManage = callerRole === 'owner' || callerRole === 'admin';
  // The server only lets the OWNER remove members (teams.ts:471), and never the
  // owner themselves (teams.ts:474). Gate the UI to match exactly so a caller is
  // never shown a control that would 403.
  const isOwner = callerRole === 'owner';
  // The caller's own internal user id, used to render the self-leave control on
  // their own row. A non-owner member (or admin) can leave; the owner cannot
  // (server blocks it, teams.ts leave route).
  const callerUserId = session?.user?.id;

  const membersQuery = useTeamMembers(teamId);
  const invitationsQuery = useTeamInvitations(teamId);
  // Armed for owners and admins only: the route 403s a plain member, and a 403
  // fetched for a block that never renders would mint a fake $exception.
  const joinRequestsQuery = useTeamJoinRequests(canManage ? teamId : undefined);
  const approveJoinMutation = useApproveJoinRequest(teamId);
  const declineJoinMutation = useDeclineJoinRequest(teamId);
  const inviteMutation = useInviteTeamMember(teamId);
  const revokeMutation = useRevokeTeamInvitation(teamId);
  const removeMutation = useRemoveTeamMember(teamId);
  const leaveMutation = useLeaveTeam(teamId);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [formError, setFormError] = useState<string | null>(null);
  // The server's refusal code, so a seat-cap or lapsed-trial refusal can offer
  // "Upgrade plan" (InviteRefusalMessage). Null for a client-side refusal.
  const [formErrorCode, setFormErrorCode] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<{ invitationId: string; email: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<
    { userId: string; name: string; role: 'owner' | 'admin' | 'member' } | null
  >(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  // A refused Approve (seat cap, lapsed plan, already resolved) reads here,
  // under the requests list, in the server's own sentence.
  const [joinRequestError, setJoinRequestError] = useState<string | null>(null);

  // Gate the submit button so an exploratory click on an empty form can't
  // dead-end on the browser's native validation bubble (invisible in replays).
  const inviteEmailValid = isValidInviteEmail(inviteEmail);

  const sortedMembers = useMemo(() => {
    const list = membersQuery.data ?? [];
    const rank: Record<'owner' | 'admin' | 'member', number> = {
      owner: 0,
      admin: 1,
      member: 2,
    };
    return [...list].sort((a, b) => {
      const r = rank[a.role] - rank[b.role];
      if (r !== 0) return r;
      const an = a.user?.name ?? a.user?.email ?? '';
      const bn = b.user?.name ?? b.user?.email ?? '';
      return an.localeCompare(bn);
    });
  }, [membersQuery.data]);

  const handleInvite = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorCode(null);
    setSuccessMessage(null);

    const trimmed = inviteEmail.trim();
    const refusal = inviteEmailError(inviteEmail);
    if (refusal) {
      setFormError(refusal);
      return;
    }

    inviteMutation.mutate(
      { email: trimmed, role: inviteRole },
      {
        onSuccess: (invitation) => {
          setInviteEmail('');
          setInviteRole('member');
          setSuccessMessage(INVITE_FORM_COPY.sent(invitation.email));
          capture('invitation_sent', { role: inviteRole, source: 'settings' });
        },
        onError: (err) => {
          // A refused invite (most often the seat cap) is a wall. Counted on the
          // same event as the project page's InviteTeamModal, so a cross-surface
          // read cannot call the quieter surface healthy. One emit per submit.
          const code = (err as { code?: string }).code ?? 'unknown';
          log.warn('[TeamMembersSection] invite refused', { code, message: err.message });
          setFormError(err.message || INVITE_FORM_COPY.sendFailed);
          setFormErrorCode(code);
          capture('invitation_send_failed', { source: 'settings', code });
        },
      },
    );
  };

  const handleApproveJoin = (requestId: string, email: string) => {
    setJoinRequestError(null);
    setSuccessMessage(null);
    approveJoinMutation.mutate(
      { requestId },
      {
        onSuccess: (result) => {
          setSuccessMessage(result.alreadyMember ? `${email} is already a member.` : `Invitation sent to ${email}.`);
          capture('team_join_request_approved', { source: 'settings' });
        },
        onError: (err) => {
          log.warn('[TeamMembersSection] approve join request refused', { requestId, message: err.message });
          setJoinRequestError(err.message || 'Failed to approve the request.');
        },
      },
    );
  };

  const handleDeclineJoin = (requestId: string) => {
    setJoinRequestError(null);
    setSuccessMessage(null);
    declineJoinMutation.mutate(
      { requestId },
      {
        onSuccess: () => {
          capture('team_join_request_declined', { source: 'settings' });
        },
        onError: (err) => {
          log.warn('[TeamMembersSection] decline join request refused', { requestId, message: err.message });
          setJoinRequestError(err.message || 'Failed to decline the request.');
        },
      },
    );
  };

  const handleRevoke = (invitationId: string, email: string) => {
    setFormError(null);
    setSuccessMessage(null);
    setConfirmRevoke({ invitationId, email });
  };

  const confirmRevokeInvitation = () => {
    if (!confirmRevoke) return;
    const { invitationId, email } = confirmRevoke;
    revokeMutation.mutate(
      { invitationId },
      {
        onSuccess: () => {
          setSuccessMessage(`Invitation for ${email} revoked.`);
        },
        onError: (err) => {
          setFormError(err.message || 'Failed to revoke invitation.');
        },
        onSettled: () => setConfirmRevoke(null),
      },
    );
  };

  const handleRemove = (userId: string, name: string, role: 'owner' | 'admin' | 'member') => {
    setFormError(null);
    setSuccessMessage(null);
    setConfirmRemove({ userId, name, role });
  };

  const confirmRemoveMember = () => {
    if (!confirmRemove) return;
    const { userId, name, role } = confirmRemove;
    removeMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          setSuccessMessage(`Removed ${name} from the team.`);
          capture('member_removed', { role });
        },
        onError: (err) => {
          setFormError(err.message || 'Failed to remove member.');
        },
        onSettled: () => setConfirmRemove(null),
      },
    );
  };

  const handleLeave = () => {
    setFormError(null);
    setSuccessMessage(null);
    setConfirmLeave(true);
  };

  const confirmLeaveTeam = () => {
    leaveMutation.mutate(undefined, {
      onSuccess: () => {
        // The hook invalidates ['app-session'] + members, so the active team
        // re-resolves in place to the caller's default team — no navigation.
        if (callerRole) capture('member_left', { role: callerRole });
        setConfirmLeave(false);
      },
      onError: (err) => {
        setFormError(err.message || 'Failed to leave the team.');
        setConfirmLeave(false);
      },
    });
  };

  if (sessionLoading || !teamId) {
    return <p className="text-gray-500 text-sm">Loading team...</p>;
  }

  return (
    <>
    <div className="space-y-6">
      {/* Members list */}
      <div>
        <h3 className="section-label mb-3">Members</h3>
        {membersQuery.isLoading ? (
          <p className="text-gray-500 text-sm">Loading members...</p>
        ) : membersQuery.error ? (
          <LoadError
            compact
            resource="members"
            error={membersQuery.error as Error}
            onRetry={() => membersQuery.refetch()}
          />
        ) : sortedMembers.length === 0 ? (
          <p className="text-gray-500 text-sm">No members yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden">
            {sortedMembers.map((member) => (
              <li
                key={member.id}
                className="flex items-center justify-between gap-3 px-4 py-3 bg-white"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar id={member.user?.id} src={member.user?.avatarUrl} name={member.user?.name || member.user?.email} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {member.user?.name || member.user?.email || 'Unknown user'}
                    </p>
                    {member.user?.email && member.user.name && (
                      <p className="text-xs text-gray-500 truncate">{member.user.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <RoleBadge role={member.role} />
                  {isOwner && member.role !== 'owner' && (
                    <button
                      type="button"
                      onClick={() =>
                        handleRemove(
                          member.userId,
                          member.user?.name || member.user?.email || 'this member',
                          member.role,
                        )
                      }
                      disabled={removeMutation.isPending}
                      className="btn-secondary text-xs"
                    >
                      <span className="text-cap-trim">Remove</span>
                    </button>
                  )}
                  {!isOwner && member.userId === callerUserId && (
                    <button
                      type="button"
                      onClick={handleLeave}
                      disabled={leaveMutation.isPending}
                      className="btn-secondary text-xs"
                    >
                      <span className="text-cap-trim">Leave team</span>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Requests to join (spec 2026-09-10): same-domain signups asking in; owners/admins only */}
      {canManage && (
        <div>
          <h3 className="section-label mb-3">Requests to join</h3>
          {joinRequestsQuery.isLoading ? (
            <p className="text-gray-500 text-sm">Loading requests...</p>
          ) : joinRequestsQuery.error ? (
            <LoadError
              compact
              resource="join requests"
              error={joinRequestsQuery.error as Error}
              onRetry={() => joinRequestsQuery.refetch()}
            />
          ) : (joinRequestsQuery.data ?? []).length === 0 ? (
            <p className="text-gray-500 text-sm">No requests to join.</p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden">
              {(joinRequestsQuery.data ?? []).map((request) => (
                <li key={request.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-white">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {request.requester.name || request.requester.email}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {request.requester.email} · {formatAskedAgo(request.createdAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleApproveJoin(request.id, request.requester.email)}
                      disabled={approveJoinMutation.isPending || declineJoinMutation.isPending}
                      className="btn-primary text-xs"
                    >
                      <span className="text-cap-trim">{approveJoinMutation.isPending ? 'Approving...' : 'Approve'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeclineJoin(request.id)}
                      disabled={approveJoinMutation.isPending || declineJoinMutation.isPending}
                      className="btn-secondary text-xs"
                    >
                      <span className="text-cap-trim">{declineJoinMutation.isPending ? 'Declining...' : 'Decline'}</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {joinRequestError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {joinRequestError}
            </p>
          )}
        </div>
      )}

      {/* Pending invitations — only visible to owners/admins */}
      {canManage && (
        <div>
          <h3 className="section-label mb-3">Pending invitations</h3>
          {invitationsQuery.isLoading ? (
            <p className="text-gray-500 text-sm">Loading invitations...</p>
          ) : invitationsQuery.error ? (
            <LoadError
              compact
              resource="invitations"
              error={invitationsQuery.error as Error}
              onRetry={() => invitationsQuery.refetch()}
            />
          ) : (invitationsQuery.data ?? []).length === 0 ? (
            <p className="text-gray-500 text-sm">No pending invitations.</p>
          ) : (
            <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden">
              {(invitationsQuery.data ?? []).map((invitation) => (
                <li
                  key={invitation.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-white"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{invitation.email}</p>
                    <p className="text-xs text-gray-500">
                      Invited as {invitation.role} · {formatRelative(invitation.expiresAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(invitation.id, invitation.email)}
                    disabled={revokeMutation.isPending}
                    className="btn-secondary text-xs"
                  >
                    <span className="text-cap-trim">{revokeMutation.isPending ? 'Revoking...' : 'Revoke'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Invite form — only owners/admins */}
      {canManage && (
        <div>
          <h3 className="section-label mb-3">Invite a new member</h3>
          <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={handleInvite}>
            <div className="flex-1">
              <label htmlFor="invite-email" className="sr-only">
                {INVITE_FORM_COPY.emailLabel}
              </label>
              <input
                id="invite-email"
                type="email"
                autoComplete="off"
                className="input input-lg w-full"
                placeholder={INVITE_FORM_COPY.emailPlaceholder}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviteMutation.isPending}
                required
              />
            </div>
            <div>
              <label htmlFor="invite-role" className="sr-only">
                {INVITE_FORM_COPY.roleLabel}
              </label>
              <SelectDropdown
                id="invite-role"
                value={inviteRole}
                onChange={(value) => setInviteRole(value as 'admin' | 'member')}
                disabled={inviteMutation.isPending}
                options={INVITE_ROLE_OPTIONS}
                // 40px tier with the field and the button: one control height
                // across a standalone form row (.input-lg + .btn-lg).
                size="lg"
              />
            </div>
            <button
              type="submit"
              className="btn-primary btn-lg"
              disabled={inviteMutation.isPending || !inviteEmailValid}
              title={inviteEmailValid ? undefined : INVITE_FORM_COPY.submitDisabledTitle}
            >
              <span className="text-cap-trim">{inviteMutation.isPending ? INVITE_FORM_COPY.submitting : INVITE_FORM_COPY.submit}</span>
            </button>
          </form>
          {formError && (
            <InviteRefusalMessage message={formError} code={formErrorCode} source="settings" />
          )}
          {successMessage && (
            <p className="mt-2 text-xs text-green-700" role="status">
              {successMessage}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            {INVITE_FORM_COPY.helper}
          </p>
        </div>
      )}

      {!canManage && (
        <p className="text-xs text-gray-500">
          Only team owners and admins can invite new members.
        </p>
      )}
    </div>

    <ConfirmDialog
      open={confirmRevoke !== null}
      title="Revoke invitation"
      message={`Revoke invitation for ${confirmRevoke?.email ?? 'this person'}? They will no longer be able to join the team with this link.`}
      confirmLabel="Revoke"
      onConfirm={confirmRevokeInvitation}
      onCancel={() => setConfirmRevoke(null)}
      destructive
      loading={revokeMutation.isPending}
    />

    <ConfirmDialog
      open={confirmRemove !== null}
      title={`Remove ${confirmRemove?.name ?? 'this member'}?`}
      message={`${confirmRemove?.name ?? 'This member'} will immediately lose access to this team’s projects and issues. Their captured issues stay with the team. You can re-invite them anytime.`}
      confirmLabel="Remove member"
      onConfirm={confirmRemoveMember}
      onCancel={() => setConfirmRemove(null)}
      destructive
      loading={removeMutation.isPending}
    />

    <ConfirmDialog
      open={confirmLeave}
      title="Leave this team?"
      message="You’ll lose access to this team’s projects and issues. The issues you captured stay with the team. You’ll need a new invite to rejoin."
      confirmLabel="Leave team"
      onConfirm={confirmLeaveTeam}
      onCancel={() => setConfirmLeave(false)}
      destructive
      loading={leaveMutation.isPending}
    />
    </>
  );
}
